import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { PasskeyCancelled, passkeysSupported, registerPasskey } from '../auth/passkeys.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';

/** Passkey management, shown in security settings. */
export function PasskeySection(): ReactNode {
  const { t, translator } = useI18n();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const passkeys = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => api.get('/api/v1/auth/passkeys'),
  });

  const add = useMutation({
    mutationFn: () => registerPasskey(),
    onSuccess: async () => {
      setNotice(null);
      await queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    },
    onError: (error: unknown) => {
      // A dismissed prompt is a deliberate act, not a failure to report as one.
      setNotice(error instanceof PasskeyCancelled ? t('passkey.cancelled') : null);
    },
  });

  const remove = useMutation({
    mutationFn: (passkeyId: string) =>
      api.delete('/api/v1/auth/passkeys/{passkeyId}', { passkeyId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['passkeys'] });
    },
  });

  const items = passkeys.data?.items ?? [];

  return (
    <section className="card" aria-labelledby="passkeys-heading">
      <h2 className="card__title" id="passkeys-heading">
        {t('passkey.title')}
      </h2>

      <p className="empty__body">{t('passkey.explainer')}</p>

      {notice === null ? null : <Alert tone="info">{notice}</Alert>}
      {add.error instanceof ApiError ? (
        <Alert tone="critical">{t(`error.${add.error.code}`)}</Alert>
      ) : null}

      {items.length === 0 && !passkeys.isPending ? (
        <p className="empty__body">{t('passkey.none')}</p>
      ) : null}

      <ul className="session-list" role="list">
        {items.map((passkey) => (
          <li key={passkey.id} className="session">
            <div className="session__detail">
              <span className="session__agent">{passkey.label}</span>
              <span className="session__meta">
                {passkey.deviceType === 'multiDevice'
                  ? t('passkey.synced')
                  : t('passkey.singleDevice')}
                {' · '}
                {passkey.lastUsedAt === null
                  ? t('passkey.neverUsed')
                  : t('passkey.lastUsed', {
                      when: translator.formatRelativeTime(new Date(passkey.lastUsedAt)),
                    })}
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={() => remove.mutate(passkey.id)}
              loading={remove.isPending && remove.variables === passkey.id}
            >
              {t('passkey.remove')}
            </Button>
          </li>
        ))}
      </ul>

      {passkeysSupported() ? (
        <Button variant="primary" loading={add.isPending} onClick={() => add.mutate()}>
          {t('passkey.add')}
        </Button>
      ) : (
        <p className="empty__body">{t('passkey.unsupported')}</p>
      )}
    </section>
  );
}
